import api from './api';

const STORAGE_KEY = 'aiPromptConfigs';
let useLocalOnly = false;
const DEFAULT_PLACEHOLDER_PROMPTS = [
  {
    _id: 'placeholder-ai-rfp-default',
    promptName: 'Default RFP Structured Prompt',
    promptContent:
      'Generate concise procurement-ready sections for Scope, Technical, Eligibility, and Commercial based on input payload.',
    sectionMapping: ['scope', 'technical', 'eligibility', 'commercial'],
    isActive: true,
    isPlaceholder: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const normalizePrompt = (prompt = {}) => ({
  _id: prompt._id || String(Date.now() + Math.random()),
  promptName: prompt.promptName || '',
  promptContent: prompt.promptContent || '',
  sectionMapping: Array.isArray(prompt.sectionMapping)
    ? prompt.sectionMapping
    : [],
  isActive: Boolean(prompt.isActive),
  createdAt: prompt.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const getLocalPrompts = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const setLocalPrompts = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

class AiPromptConfigService {
  static async getPrompts() {
    if (useLocalOnly) {
      return { data: getLocalPrompts().map(normalizePrompt) };
    }

    try {
      const response = await api.get('/sourcing/ai-prompts');
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      const normalized = list.map(normalizePrompt);
      // Frontend placeholder seed to keep AI config testable on empty datasets.
      return {
        data: normalized.length ? normalized : DEFAULT_PLACEHOLDER_PROMPTS,
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
      }
      const local = getLocalPrompts().map(normalizePrompt);
      return {
        data: local.length ? local : DEFAULT_PLACEHOLDER_PROMPTS,
      };
    }
  }

  static async createPrompt(payload) {
    const prompt = normalizePrompt(payload);

    if (useLocalOnly) {
      const current = getLocalPrompts().map(normalizePrompt);
      const next = [prompt, ...current];
      setLocalPrompts(next);
      return { success: true, data: prompt };
    }

    try {
      const response = await api.post('/sourcing/ai-prompts', prompt);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
      }
      const current = getLocalPrompts().map(normalizePrompt);
      const next = [prompt, ...current];
      setLocalPrompts(next);
      return { success: true, data: prompt };
    }
  }

  static async updatePrompt(id, payload) {
    const updated = normalizePrompt({ ...payload, _id: id });

    if (useLocalOnly) {
      const current = getLocalPrompts().map(normalizePrompt);
      const next = current.map((item) => (item._id === id ? updated : item));
      setLocalPrompts(next);
      return { success: true, data: updated };
    }

    try {
      const response = await api.put(`/sourcing/ai-prompts/${id}`, updated);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
      }
      const current = getLocalPrompts().map(normalizePrompt);
      const next = current.map((item) => (item._id === id ? updated : item));
      setLocalPrompts(next);
      return { success: true, data: updated };
    }
  }

  static async activatePrompt(id) {
    const all = (await this.getPrompts()).data || [];
    const next = all.map((item) => ({ ...item, isActive: item._id === id }));

    if (useLocalOnly) {
      setLocalPrompts(next.map(normalizePrompt));
      return { success: true };
    }

    try {
      await Promise.all(
        next.map((item) =>
          api.put(`/sourcing/ai-prompts/${item._id}`, normalizePrompt(item)),
        ),
      );
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
      }
      setLocalPrompts(next.map(normalizePrompt));
    }

    return { success: true };
  }

  static async deletePrompt(id) {
    if (useLocalOnly) {
      const current = getLocalPrompts().map(normalizePrompt);
      setLocalPrompts(current.filter((item) => item._id !== id));
      return { success: true };
    }

    try {
      const response = await api.delete(`/sourcing/ai-prompts/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) {
        useLocalOnly = true;
      }
      const current = getLocalPrompts().map(normalizePrompt);
      setLocalPrompts(current.filter((item) => item._id !== id));
      return { success: true };
    }
  }

  static async getActivePrompt() {
    const all = (await this.getPrompts()).data || [];
    return all.find((item) => item.isActive) || null;
  }
}

export default AiPromptConfigService;
